import { ChakraV3, stringParam, useQueryParams } from '@piccolohealth/ui';
import { P, z, zodPassword } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegCheckCircle } from 'react-icons/fa';
import { Navigate, useNavigate } from 'react-router-dom';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../context/AuthContext';
import { asPiccoloError } from '../../utils/errors';
import { zodResolver } from '../../utils/zod';
import { AuthCard } from './components/AuthCard';
import { AuthLayout } from './components/AuthLayout';
import { BackToLoginButton } from './components/BackToLoginButton';

type FormValues = {
  password: string;
  confirmPassword: string;
};

const schema: z.ZodSchema<FormValues> = z
  .object({
    password: zodPassword,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const ResetPassword = () => {
  const [params] = useQueryParams({
    oobCode: stringParam,
    email: stringParam,
  });
  const { oobCode, email } = params;

  const { resetPassword, logout } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    logout();
  }, [logout]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    if (!oobCode || !email) {
      return;
    }

    try {
      await resetPassword(oobCode, email, data.password);
    } catch (error) {
      const piccoloError = asPiccoloError(error);
      methods.reset({ password: '', confirmPassword: '' });
      methods.setError('password', { message: piccoloError.message });
    }
  };

  const onClickLogin = () => {
    void navigate('/login');
  };

  if (!oobCode) {
    return <Navigate to='/login' />;
  }

  const content = P.run(() => {
    if (methods.formState.isSubmitSuccessful) {
      return (
        <AuthCard>
          <ChakraV3.Stack gap={4} align='center'>
            <ChakraV3.Icon color='success' boxSize={16}>
              <FaRegCheckCircle />
            </ChakraV3.Icon>
            <ChakraV3.Text fontSize='sm' textAlign='center'>
              Your password has been reset successfully. Please login with your new password.
            </ChakraV3.Text>
            <ChakraV3.Button w='full' size='md' colorPalette='brand' onClick={onClickLogin}>
              Login
            </ChakraV3.Button>
          </ChakraV3.Stack>
        </AuthCard>
      );
    }

    return (
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Reset Password
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Please enter your new password below
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Stack gap={4}>
              <HookedFormField name='password' label='New Password'>
                <HookedInput
                  name='password'
                  type='password'
                  size='md'
                  placeholder='Enter new password'
                />
              </HookedFormField>
              <HookedFormField name='confirmPassword' label='Confirm Password'>
                <HookedInput
                  name='confirmPassword'
                  type='password'
                  size='md'
                  placeholder='Confirm new password'
                />
              </HookedFormField>

              <HookedSubmitButton size='md' mt={4} loadingText='Resetting password...'>
                Reset Password
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </form>
        </FormProvider>
        <BackToLoginButton />
      </AuthCard>
    );
  });

  return <AuthLayout>{content}</AuthLayout>;
};
